import * as amplitude from "@amplitude/analytics-browser";
import { GeneralPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { NotificationBannerItem } from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { nativeRandom, randomHexString } from "@redotech/util/random";
import { useEffect } from "react";
import { PlatformEvents } from "../../../platform-events";
import { ActionRequiredSeverity } from "./action-required-notification";

export function useWalmartOmsReauthorization(): {
  severity: ActionRequiredSeverity;
  item: NotificationBannerItem;
} | null {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const canAuthenticateWithThirdParties = useHasPermission(
    GeneralPermissions.AUTHENTICATE_WITH_THIRD_PARTIES,
  );

  const isTokenValid = useLoad(async () => {
    if (!canAuthenticateWithThirdParties) {
      return undefined;
    }
    const result = await rpcClient.getWalmartIntegration({});
    return result.valid;
  }, [rpcClient, canAuthenticateWithThirdParties]);

  // Only fetch signed state when we know token is invalid
  const signedStateLoad = useLoad(async () => {
    if (isTokenValid.value !== false) {
      return undefined;
    }
    const result = await rpcClient.getLegacySignedOAuthState({});
    return result.signedState;
  }, [rpcClient, isTokenValid.value]);

  const reauthenticateLink = (() => {
    if (isTokenValid.value !== false) {
      return undefined;
    }
    if (!process.env.WALMART_APP_CLIENT_ID || !signedStateLoad.value) {
      return undefined;
    }

    const callbackUrl = new URL("https://login.account.wal-mart.com/authorize");
    const nonce = randomHexString(nativeRandom, 20);

    callbackUrl.searchParams.append("responseType", "code");
    callbackUrl.searchParams.append(
      "clientId",
      process.env.WALMART_APP_CLIENT_ID,
    );
    callbackUrl.searchParams.append(
      "redirectUri",
      "https://app-server.getredo.com/integrations/walmart/callback",
    );
    callbackUrl.searchParams.append("clientType", "seller");
    callbackUrl.searchParams.append("nonce", nonce);
    callbackUrl.searchParams.append("state", signedStateLoad.value);

    return callbackUrl.toString();
  })();

  const isBannerVisible = !!reauthenticateLink;

  useEffect(() => {
    if (isBannerVisible) {
      amplitude.track(PlatformEvents.WALMART_REAUTHORIZE_VIEWED);
    }
  }, [isBannerVisible]);

  if (isTokenValid.value !== false) {
    return null;
  }

  // Wait for signed state to load before showing the banner
  if (!reauthenticateLink) {
    return null;
  }

  return {
    severity: ActionRequiredSeverity.Medium,
    item: {
      id: "walmart-oms-reauthorization",
      type: "warning",
      message:
        "Your Walmart OMS connection needs to be reauthorized to continue using Redo OMS.",
      actions: [
        {
          actionType: "link",
          href: reauthenticateLink,
          text: "Reauthorize",
          internal: false,
          onClick: () =>
            amplitude.track(PlatformEvents.WALMART_REAUTHORIZE_CLICKED),
        },
      ],
    },
  };
}
